import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { toast } from 'sonner';

import {
  type BulkImageAction,
  type BulkImageActionResult,
  runBulkImageAction,
} from '@/lib/mediaLibrary/bulkActions';
import {
  estimateDownloadBytes,
  formatBytes,
  getMediaDownloadUrl,
  isMediaFailed,
  isMediaOwnedBy,
  isMediaSelectable,
  type MediaItem,
  planDownloadBatch,
} from '@/lib/mediaLibrary/mediaItems';
import {
  createMediaSelectionStore,
  type MediaSelectionStore,
} from '@/lib/mediaLibrary/selectionStore';
import {
  archiveBaseName,
  canStreamToDisk,
  downloadMedia,
  type DownloadProgress,
  type MediaDownloadResult,
  pickZipSaveTarget,
  STREAM_TO_DISK_THRESHOLD_BYTES,
} from '@/lib/mediaLibrary/zipDownload';

import ButtonLink from '@/components/links/ButtonLink';

import { useAnalytics } from '@/contexts/Analytics';
import axiosInstance from '@/utils/httpClient';
import { toastError } from '@/utils/toastError';
import { useUser } from '@/utils/useUser';

import MediaSelectionBar from './MediaSelectionBar';

// Multi-select for media grids (Generated Images, History > Images/Videos,
// Favorites, Trash). Tiles opt in through useMediaTileSelection; without a
// provider they render exactly as before.

export type MediaLibraryMode = 'library' | 'trash';

export type MediaLibraryTask =
  | { kind: 'download'; progress: DownloadProgress }
  | { kind: 'listing'; loaded: number; total: number }
  | {
      kind: 'trash' | 'restore' | 'purge' | 'favorite' | 'unfavorite';
      count: number;
    };

export type LoadAllMediaItems = (
  onProgress: (loaded: number, total: number) => void,
  signal: AbortSignal,
) => Promise<MediaItem[]>;

export interface MediaSelectionProviderProps {
  children: ReactNode;
  /** Items on screen, in display order (select all, shift-click ranges). */
  items: MediaItem[];
  /** Everything matching the current filters; enables "Select all N". */
  totalCount?: number;
  /** Loads every item matching the current filters ("Select all N"). */
  loadAllItems?: LoadAllMediaItems;
  /** The selection is cleared whenever this changes (filters, search, tab). */
  resetKey?: string;
  mode?: MediaLibraryMode;
  /** Tiles are always in selection mode (Trash). */
  alwaysSelecting?: boolean;
  /** Items left this list: trashed from the library, or restored from Trash. */
  onItemsRemoved?: (ids: number[]) => void;
  /** An Undo brought removed items back. */
  onItemsReturned?: (ids: number[]) => void;
  onItemsChanged?: (ids: number[], patch: Partial<MediaItem>) => void;
  /** The action bar centres itself over this element (default: viewport). */
  anchorRef?: RefObject<HTMLElement | null>;
  /** Archive names: `<prefix>-2026-10-05.zip`. */
  archivePrefix?: string;
  /** Analytics: which surface the action came from. */
  source: string;
}

interface TileContextValue {
  store: MediaSelectionStore;
  alwaysSelecting: boolean;
  toggle: (item: MediaItem, range: boolean) => void;
}

export interface MediaSelectionActions {
  toggle: (item: MediaItem, range: boolean) => void;
  selectVisible: () => void;
  selectFailed: () => void;
  selectAllMatching: () => Promise<void>;
  clear: () => void;
  enterSelectionMode: () => void;
  trashSelected: () => Promise<void>;
  restoreSelected: () => Promise<void>;
  /** Trash: delete the selected items for good (files gone, credits still count). */
  purgeSelected: () => Promise<void>;
  setFavoriteSelected: (value: boolean) => Promise<void>;
  downloadSelected: () => Promise<void>;
  cancelTask: () => void;
}

export interface MediaSelectionContextValue {
  store: MediaSelectionStore;
  mode: MediaLibraryMode;
  alwaysSelecting: boolean;
  visibleItems: MediaItem[];
  totalCount?: number;
  canLoadAll: boolean;
  currentUserId?: string;
  task: MediaLibraryTask | null;
  anchorRef?: RefObject<HTMLElement | null>;
  actions: MediaSelectionActions;
}

const TileContext = createContext<TileContextValue | null>(null);
const SelectionContext = createContext<MediaSelectionContextValue | null>(null);

const noopSubscribe = () => () => undefined;
const EMPTY_ITEMS: MediaItem[] = [];

const isAbortError = (error: unknown) =>
  (error as { name?: string })?.name === 'AbortError';

const postBulkAction = async (body: {
  action: BulkImageAction;
  image_ids: number[];
}): Promise<BulkImageActionResult> => {
  const response = await axiosInstance.post('/api/images/bulk', body, {
    skipErrorToast: true,
  });
  return response.data as BulkImageActionResult;
};

const ownIds = (items: MediaItem[], userId?: string) =>
  items
    .filter((item) => isMediaOwnedBy(item, userId))
    .map((item) => item.id as number);

export const MediaSelectionProvider = ({
  children,
  items,
  totalCount,
  loadAllItems,
  resetKey,
  mode = 'library',
  alwaysSelecting = false,
  onItemsRemoved,
  onItemsReturned,
  onItemsChanged,
  anchorRef,
  archivePrefix,
  source,
}: MediaSelectionProviderProps) => {
  const { t } = useLocale('media-library');
  const { user, isFreePlan, userDetails } = useUser();
  const analytics = useAnalytics();
  const [store] = useState(createMediaSelectionStore);
  const [task, setTask] = useState<MediaLibraryTask | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Handlers read the latest props through a ref so the action object (and
  // the tile context) stays referentially stable across renders.
  const latest = useRef({
    items,
    loadAllItems,
    onItemsRemoved,
    onItemsReturned,
    onItemsChanged,
    userId: user?.id,
    downloadsLocked: false,
  });
  latest.current = {
    items,
    loadAllItems,
    onItemsRemoved,
    onItemsReturned,
    onItemsChanged,
    userId: user?.id,
    // Same gate as the single-image Download button.
    downloadsLocked: isFreePlan && userDetails?.bonus_images === 0,
  };

  const firstReset = useRef(true);
  useEffect(() => {
    if (firstReset.current) {
      firstReset.current = false;
      return;
    }
    store.clear();
  }, [resetKey, store]);

  // Abort a running export when the page unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const toggle = useCallback(
    (item: MediaItem, range: boolean) =>
      store.toggle(item, latest.current.items, range),
    [store],
  );

  const tileValue = useMemo<TileContextValue>(
    () => ({ store, alwaysSelecting, toggle }),
    [store, alwaysSelecting, toggle],
  );

  const showUpgradeToast = useCallback(() => {
    toast.warning(
      <div>
        <div className='mb-5'>
          {t(
            'Downloading is available on paid plans. Upgrade to download your images and videos.',
          )}
        </div>
        <ButtonLink variant='dark' href='/settings/billing'>
          {t('Upgrade plan')}
        </ButtonLink>
      </div>,
    );
  }, [t]);

  /**
   * Downloads one batch as a ZIP (or a single file). Resolves with the result,
   * or null when the user cancelled or the download failed. `leftAfter`:
   * selected items waiting for the next batch, for the toast.
   */
  const runDownload = useCallback(
    async (
      downloadable: MediaItem[],
      leftAfter = 0,
    ): Promise<MediaDownloadResult | null> => {
      const prefix =
        archivePrefix ||
        (downloadable.some((item) => item.is_video)
          ? 'easy-peasy-media'
          : 'easy-peasy-images');
      const baseName = archiveBaseName(prefix);

      // Large exports stream to a file the user picks. This must stay the
      // first await after the click: the picker needs its user activation.
      let handle: Awaited<ReturnType<typeof pickZipSaveTarget>> = null;
      if (
        downloadable.length > 1 &&
        canStreamToDisk() &&
        estimateDownloadBytes(downloadable) > STREAM_TO_DISK_THRESHOLD_BYTES
      ) {
        try {
          const picked = await pickZipSaveTarget(`${baseName}.zip`);
          if (!picked) return null; // The user closed the save dialog.
          handle = picked;
        } catch {
          handle = null; // Picker unavailable: build the ZIP in memory.
        }
      }

      const controller = new AbortController();
      abortRef.current = controller;
      const startedAt = Date.now();
      setTask({
        kind: 'download',
        progress: {
          filesDone: 0,
          filesTotal: downloadable.length,
          failed: 0,
          bytes: 0,
          part: 1,
        },
      });

      try {
        const result = await downloadMedia({
          items: downloadable,
          baseName,
          fileHandle: handle,
          signal: controller.signal,
          onProgress: (progress) =>
            setTask((current) =>
              current?.kind === 'download' ? { ...current, progress } : current,
            ),
        });

        const left = leftAfter + result.remaining.length;
        analytics.track('Media Bulk Downloaded', {
          source,
          count: downloadable.length,
          files: result.files,
          failed: result.failed.length,
          left,
          bytes: result.bytes,
          parts: result.parts,
          mode: result.mode,
          duration_ms: Date.now() - startedAt,
        });

        const nextBatch = left
          ? t('{{count}} more to go. Click Download for the next batch.', {
              count: left,
            })
          : undefined;
        if (!result.files) {
          toast.error(
            t(
              "We couldn't download these files. Please try again in a moment.",
            ),
          );
        } else if (result.failed.length) {
          toast.warning(
            t('Downloaded {{done}} of {{total}} files', {
              done: result.files,
              total: result.files + result.failed.length,
            }),
            {
              description:
                [
                  result.mode === 'single'
                    ? undefined
                    : t('The rest are listed in missing-files.txt in the ZIP.'),
                  nextBatch,
                ]
                  .filter(Boolean)
                  .join(' ') || undefined,
            },
          );
        } else {
          toast.success(
            t('Downloaded {{count}} files', { count: result.files }),
            {
              description:
                nextBatch ??
                (result.parts > 1
                  ? t('{{size}} in {{parts}} ZIP files', {
                      size: formatBytes(result.bytes),
                      parts: result.parts,
                    })
                  : formatBytes(result.bytes)),
            },
          );
        }
        return result;
      } catch (error) {
        if (isAbortError(error)) toast(t('Download cancelled'));
        else toastError(error);
        return null;
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setTask(null);
      }
    },
    [analytics, archivePrefix, source, t],
  );

  const runBulk = useCallback(
    async (
      action: BulkImageAction,
      ids: number[],
      kind: Exclude<MediaLibraryTask['kind'], 'download' | 'listing'>,
    ) => {
      setTask({ kind, count: ids.length });
      try {
        return await runBulkImageAction(action, ids, postBulkAction);
      } finally {
        setTask(null);
      }
    },
    [],
  );

  const actions = useMemo<MediaSelectionActions>(() => {
    // Moves ids to Trash (library) or back out of it (trash); the opposite
    // call is what the Undo button runs.
    const moveItems = async (
      ids: number[],
      direction: 'trash' | 'restore',
      isUndo = false,
    ) => {
      const action = direction === 'trash' ? 'REMOVE' : 'RESTORE';
      let result: BulkImageActionResult;
      try {
        result = await runBulk(action, ids, direction);
      } catch (error) {
        const partial = (error as { partialResult?: BulkImageActionResult })
          .partialResult;
        if (partial?.affected_ids.length) {
          applyMove(partial.affected_ids, direction, isUndo);
        }
        toastError(error);
        return;
      }
      applyMove(result.affected_ids, direction, isUndo);
      if (isUndo) return;

      const moved = result.affected_ids;
      analytics.track(
        direction === 'trash' ? 'Media Bulk Trashed' : 'Media Bulk Restored',
        { source, count: moved.length, skipped: result.skipped_ids.length },
      );
      if (!moved.length) {
        toast.error(
          direction === 'trash'
            ? t(
                'Nothing was moved to Trash. Only your own items can be deleted.',
              )
            : t('Nothing was restored. Please refresh and try again.'),
        );
        return;
      }
      toast.success(
        direction === 'trash'
          ? t('Moved {{count}} items to Trash', { count: moved.length })
          : t('Restored {{count}} items', { count: moved.length }),
        {
          description:
            direction === 'trash' ? (
              <span>
                {t('You can restore them from Trash.')}{' '}
                <Link
                  href='/history/trash?tab=media'
                  className='font-medium underline underline-offset-2'
                >
                  {t('Open Trash')}
                </Link>
              </span>
            ) : (
              t('They are back in your History.')
            ),
          duration: 10000,
          action: {
            label: t('Undo'),
            onClick: () => {
              void moveItems(
                moved,
                direction === 'trash' ? 'restore' : 'trash',
                true,
              );
            },
          },
        },
      );
    };

    // Which callback fires depends on whether the ids left or rejoined the
    // list this provider shows.
    const applyMove = (
      ids: number[],
      direction: 'trash' | 'restore',
      isUndo: boolean,
    ) => {
      if (!ids.length) return;
      const leavesList =
        (mode === 'library' && direction === 'trash') ||
        (mode === 'trash' && direction === 'restore');
      if (leavesList) {
        store.removeIds(ids);
        latest.current.onItemsRemoved?.(ids);
      } else {
        latest.current.onItemsReturned?.(ids);
      }
      if (!isUndo && leavesList) store.clear();
    };

    return {
      toggle,
      selectVisible: () => {
        const selectable = latest.current.items.filter((item) =>
          isMediaSelectable(item),
        );
        store.setMany(selectable, true);
      },
      selectFailed: () => {
        const failed = latest.current.items.filter(
          (item) => isMediaSelectable(item) && isMediaFailed(item),
        );
        store.replace(failed);
        store.setExplicitMode(true);
      },
      selectAllMatching: async () => {
        const load = latest.current.loadAllItems;
        if (!load) return;
        const controller = new AbortController();
        abortRef.current?.abort();
        abortRef.current = controller;
        setTask({ kind: 'listing', loaded: 0, total: 0 });
        try {
          const all = await load(
            (loaded, total) => setTask({ kind: 'listing', loaded, total }),
            controller.signal,
          );
          store.setExplicitMode(true);
          store.setMany(all, true);
          analytics.track('Media Select All', { source, count: all.length });
        } catch (error) {
          if (!isAbortError(error)) toastError(error);
        } finally {
          if (abortRef.current === controller) abortRef.current = null;
          setTask(null);
        }
      },
      clear: () => store.clear(),
      enterSelectionMode: () => store.setExplicitMode(true),
      trashSelected: async () => {
        const ids = ownIds(store.getItems(), latest.current.userId);
        if (ids.length) await moveItems(ids, 'trash');
      },
      restoreSelected: async () => {
        const ids = ownIds(store.getItems(), latest.current.userId);
        if (ids.length) await moveItems(ids, 'restore');
      },
      purgeSelected: async () => {
        const ids = ownIds(store.getItems(), latest.current.userId);
        if (!ids.length) return;
        // No Undo here: the files are deleted, so the toast says so instead.
        const removeFromList = (purged: number[]) => {
          if (!purged.length) return;
          store.removeIds(purged);
          latest.current.onItemsRemoved?.(purged);
        };
        let result: BulkImageActionResult;
        try {
          result = await runBulk('PURGE', ids, 'purge');
        } catch (error) {
          const partial = (error as { partialResult?: BulkImageActionResult })
            .partialResult;
          removeFromList(partial?.affected_ids || []);
          toastError(error);
          return;
        }
        const purged = result.affected_ids;
        removeFromList(purged);
        store.clear();
        analytics.track('Media Bulk Purged', {
          source,
          count: purged.length,
          skipped: result.skipped_ids.length,
        });
        if (!purged.length) {
          toast.error(t('Nothing was deleted. Please refresh and try again.'));
          return;
        }
        toast.success(
          t('Deleted {{count}} items forever', { count: purged.length }),
        );
      },
      setFavoriteSelected: async (value) => {
        const ids = ownIds(store.getItems(), latest.current.userId);
        if (!ids.length) return;
        try {
          const result = await runBulk(
            value ? 'FAVORITE' : 'UNFAVORITE',
            ids,
            value ? 'favorite' : 'unfavorite',
          );
          const changed = result.affected_ids;
          store.patchItems(changed, { is_favorite: value });
          latest.current.onItemsChanged?.(changed, { is_favorite: value });
          analytics.track(
            value ? 'Media Bulk Favorited' : 'Media Bulk Unfavorited',
            { source, count: changed.length },
          );
          toast.success(
            value
              ? t('Added {{count}} items to Favorites', {
                  count: changed.length,
                })
              : t('Removed {{count}} items from Favorites', {
                  count: changed.length,
                }),
          );
        } catch (error) {
          toastError(error);
        }
      },
      downloadSelected: async () => {
        if (latest.current.downloadsLocked) {
          showUpgradeToast();
          return;
        }
        const downloadable = store
          .getItems()
          .filter((item) => Boolean(getMediaDownloadUrl(item)));
        if (!downloadable.length) {
          toast.info(t('Failed generations have no file to download.'));
          return;
        }
        const { batch, rest } = planDownloadBatch(downloadable);
        const result = await runDownload(batch, rest.length);
        // A batch of a bigger selection: drop what was handled, so the
        // selection now holds exactly what the next click downloads.
        if (result && (rest.length || result.remaining.length)) {
          store.removeIds(
            [...result.downloaded, ...result.failed].map(
              (item) => item.id as number,
            ),
          );
        }
      },
      cancelTask: () => abortRef.current?.abort(),
    };
  }, [
    analytics,
    mode,
    runBulk,
    runDownload,
    showUpgradeToast,
    source,
    store,
    t,
    toggle,
  ]);

  const value = useMemo<MediaSelectionContextValue>(
    () => ({
      store,
      mode,
      alwaysSelecting,
      visibleItems: items,
      totalCount,
      canLoadAll: Boolean(loadAllItems),
      currentUserId: user?.id,
      task,
      anchorRef,
      actions,
    }),
    [
      store,
      mode,
      alwaysSelecting,
      items,
      totalCount,
      loadAllItems,
      user?.id,
      task,
      anchorRef,
      actions,
    ],
  );

  return (
    <TileContext.Provider value={tileValue}>
      <SelectionContext.Provider value={value}>
        {children}
        <MediaSelectionBar />
      </SelectionContext.Provider>
    </TileContext.Provider>
  );
};

/** Full context for toolbars and the action bar; null outside a provider. */
export const useMediaSelection = () => useContext(SelectionContext);

export const useMediaSelectionCount = () => {
  const context = useContext(SelectionContext);
  const store = context?.store;
  return useSyncExternalStore(
    store ? store.subscribe : noopSubscribe,
    () => (store ? store.getCount() : 0),
    () => 0,
  );
};

export const useMediaSelectionActive = () => {
  const context = useContext(SelectionContext);
  const store = context?.store;
  return useSyncExternalStore(
    store ? store.subscribe : noopSubscribe,
    () => (store ? store.isActive() || context.alwaysSelecting : false),
    () => false,
  );
};

export const useMediaSelectedItems = () => {
  const context = useContext(SelectionContext);
  const store = context?.store;
  return useSyncExternalStore(
    store ? store.subscribe : noopSubscribe,
    () => (store ? store.getItems() : EMPTY_ITEMS),
    () => EMPTY_ITEMS,
  );
};

export interface MediaTileSelection {
  /** The tile is inside a provider and can be selected. */
  enabled: boolean;
  selected: boolean;
  /** Selection mode is on: clicks select instead of opening the item. */
  active: boolean;
  toggle: (range: boolean) => void;
}

/** Per-tile selection state; re-renders only when this tile's state changes. */
export const useMediaTileSelection = (item: MediaItem): MediaTileSelection => {
  const context = useContext(TileContext);
  const store = context?.store;
  const id = typeof item.id === 'number' ? item.id : -1;
  const subscribe = store ? store.subscribe : noopSubscribe;
  const selected = useSyncExternalStore(
    subscribe,
    () => (store ? store.isSelected(id) : false),
    () => false,
  );
  const active = useSyncExternalStore(
    subscribe,
    () => (store ? store.isActive() || context.alwaysSelecting : false),
    () => false,
  );
  const enabled = Boolean(context) && isMediaSelectable(item);
  return {
    enabled,
    selected: enabled && selected,
    active,
    toggle: (range: boolean) => context?.toggle(item, range),
  };
};
